import { isIrrelevantNode } from "./isIrrelevantNode";
import { isElementNode } from "./isNode";

/**
 * Browsers have this horrible horrible bug, where they will not report line
 * breaks as being part of a range.toString() call...
 *
 * We're going to have to circumvent this by resorting to the fact that cloning
 * the range contents as a DocumentFragment, mounting that fragment into a
 * temporary node and then reading .innerText works well...
 */
export function getTextFromRange(range: Range): string {
  // Create a temporary, detached node
  const temporaryNode = document.createElement("div");

  // Make sure that the temporary node will not interfere with the flow of the
  // document
  temporaryNode.style.cssText = `
      position: absolute;
      top: -100px;
      left: -100px;
      opacity: 0.001;
  `;

  // Mount the cloned range contents into the temporary node
  temporaryNode.appendChild(range.cloneContents());

  // Remove <script /> tags from the temporary node, as these may cause issues
  // with the way that text content is being extracted by the browser...
  for (const script of Array.from(
    temporaryNode.getElementsByTagName("script")
  )) {
    if (script.parentNode != null) {
      script.parentNode.removeChild(script);
    }
  }

  // Now that we've cleaned up the content of the text range, mount the
  // temporary node into the DOM
  document.body.appendChild(temporaryNode);

  // Iterate through all children within the temporary node, and make sure
  // that everything that's tagged with user-select: none is going to be
  // ignored...
  recursivelyDropIrrelevantChildren(temporaryNode);

  // Extract text content from the node
  const text = temporaryNode.innerText;

  // Remove the temporary node instantly from the DOM again
  document.body.removeChild(temporaryNode);

  return text;
}

function recursivelyDropIrrelevantChildren(node: Node): void {
  for (const child of Array.from(node.childNodes)) {
    if (isIrrelevantNode(child)) {
      node.removeChild(child);
    } else if (isElementNode(child)) {
      recursivelyDropIrrelevantChildren(child);
    }
  }
}
